"use client";

import { useState, useEffect } from "react";
import { X, Minus, Plus, ShoppingCart, Pencil } from "lucide-react";
import { useCart } from "@/lib/cart/CartContext";
import { useLiveQuotes } from "@/lib/cart/useLiveQuotes";
import { getDistinctId } from "@/lib/analytics/client";
import { AiInspirationDisclaimer } from "@/components/shared/AiInspirationDisclaimer";
import { HorticulturalDisclaimer } from "@/components/shared/HorticulturalDisclaimer";

interface CartModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CartModal({ isOpen, onClose }: CartModalProps) {
  const cart = useCart();
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  // Explicit marketing opt-in at checkout (PLA2-373). Unchecked by default.
  const [marketingConsent] = useState(cart.metadata.marketingConsent ?? false);

  // LIVE Shopify prices + availability (D2C). When source === "PLANTISTA" the
  // quotes are empty and every derived value falls back to today's behavior.
  const {
    source,
    quotes,
    loading: quotesLoading,
  } = useLiveQuotes(cart.items, cart.metadata.retailerId);
  const isShopify = source === "SHOPIFY";

  // Per-line derived pricing/availability. For PLANTISTA these all mirror the
  // existing item.unitPrice values, so the UI is unchanged.
  const lines = cart.items.map((item) => {
    const quote = quotes[item.plant.id];
    const soldOut = isShopify && !!quote && quote.available === false;
    const unmapped = isShopify && !!quote && quote.variantId === null;
    const excluded = soldOut || unmapped;
    const effectiveUnitPrice = isShopify
      ? (quote?.unitPrice ?? item.unitPrice)
      : item.unitPrice;
    const priceLoading = isShopify && quotesLoading && !quote;
    return {
      item,
      quote,
      soldOut,
      unmapped,
      excluded,
      effectiveUnitPrice,
      priceLoading,
    };
  });

  const displaySubtotal = isShopify
    ? lines.reduce(
        (sum, l) =>
          !l.excluded && l.effectiveUnitPrice !== null
            ? sum + l.effectiveUnitPrice * l.item.quantity
            : sum,
        0
      )
    : cart.subtotal;

  // Items that can actually be purchased (drive the checkout button + payload).
  const purchasableCount = isShopify
    ? lines.filter((l) => !l.excluded && l.effectiveUnitPrice !== null).length
    : cart.items.filter((i) => i.unitPrice !== null).length;

  // Pre-fill email from cart metadata (set during add-to-cart from the design page)
  useEffect(() => {
    if (cart.metadata.customerEmail && !email) {
      setEmail(cart.metadata.customerEmail);
    }
  }, [cart.metadata.customerEmail]);

  const handleCheckout = async () => {
    if (!email || !email.includes("@")) {
      setEmailError("Please enter a valid email address");
      return;
    }
    setEmailError("");
    setIsCheckingOut(true);

    // Open the checkout tab synchronously, inside the click gesture — opening it
    // after the fetch resolves would be caught by popup blockers.
    const checkoutTab = window.open("", "_blank");
    if (checkoutTab) checkoutTab.opener = null;

    try {
      // For SHOPIFY, drop sold-out/unmapped lines and send the LIVE unit price
      // (server re-resolves + Shopify reprices; this just keeps analytics honest).
      // For PLANTISTA this reduces to today's "items with a price" filter.
      const checkoutLines = lines
        .filter((l) =>
          isShopify
            ? !l.excluded && l.effectiveUnitPrice !== null
            : l.item.unitPrice !== null
        )
        .map((l) => ({
          plantId: l.item.genusAliasId,
          name: l.item.plant.commonName,
          scientificName: l.item.plant.scientificName,
          quantity: l.item.quantity,
          unitPrice: isShopify ? l.effectiveUnitPrice : l.item.unitPrice,
          imageUrl: l.item.plant.imageUrl || null,
          externalVariantId: l.item.externalVariantId ?? null,
        }));

      if (checkoutLines.length === 0) {
        checkoutTab?.close();
        alert("No items with prices available for checkout.");
        setIsCheckingOut(false);
        return;
      }

      const response = await fetch("/api/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: checkoutLines,
          customerEmail: email,
          retailerId: cart.metadata.retailerId,
          projectId: cart.metadata.projectId,
          recipeId: cart.metadata.recipeId,
          marketingConsent,
          // PLA2-973: container-flow attribution + the browser's PostHog id,
          // so the order joins the session that designed it.
          flow: cart.metadata.flow,
          planterId: cart.metadata.planterId,
          analyticsDistinctId: getDistinctId(),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Checkout failed");
      }

      // Hosted checkout (Stripe / Shopify) opens in a new tab; fall back to
      // same-tab navigation if the popup was blocked.
      if (data.url) {
        if (checkoutTab) {
          checkoutTab.location.href = data.url;
          setIsCheckingOut(false);
        } else {
          window.location.href = data.url;
        }
      } else {
        checkoutTab?.close();
        setIsCheckingOut(false);
      }
    } catch (error) {
      checkoutTab?.close();
      console.error("[Cart] Checkout error:", error);
      alert(
        error instanceof Error
          ? error.message
          : "Checkout failed. Please try again."
      );
      setIsCheckingOut(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      {/* Slide-over panel */}
      <div className="relative w-full max-w-md bg-plantista-50 dark:bg-plantista-950 shadow-xl flex flex-col animate-in slide-in-from-right duration-300">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-plantista-800">
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-plantista-600 dark:text-plantista-400" />
            <h2 className="text-lg font-bold font-display text-plantista-950 dark:text-white">
              Shopping Cart
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-plantista-950 dark:hover:text-white transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {cart.items.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingCart className="w-12 h-12 mx-auto text-plantista-300 dark:text-plantista-700 mb-4" />
              <p className="text-plantista-600 dark:text-plantista-400">
                Your cart is empty
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {lines.map(
                ({
                  item,
                  soldOut,
                  unmapped,
                  excluded,
                  effectiveUnitPrice,
                  priceLoading,
                }) => (
                  <div
                    key={item.genusAliasId}
                    className={`bg-white dark:bg-plantista-900 border border-gray-200 dark:border-plantista-800 rounded-lg p-3 flex items-start gap-3${
                      excluded ? " opacity-60" : ""
                    }`}
                  >
                    {item.plant.imageUrl ? (
                      <img
                        src={item.plant.imageUrl}
                        alt={item.plant.commonName}
                        className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-gray-200 dark:bg-plantista-800 flex-shrink-0" />
                    )}

                    <div className="flex-1 min-w-0">
                      {/* Full name wraps; sold-out badge flows inline after it */}
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-semibold text-sm leading-snug text-plantista-950 dark:text-white">
                          {item.plant.commonName}
                          {soldOut && (
                            <span className="ml-1.5 inline-block align-middle px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400">
                              Sold out
                            </span>
                          )}
                        </p>
                        <button
                          onClick={() => cart.removeItem(item.genusAliasId)}
                          className="-mt-0.5 text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition flex-shrink-0"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      {priceLoading ? (
                        <div className="mt-1 h-3 w-16 rounded bg-gray-200 dark:bg-plantista-800 animate-pulse" />
                      ) : unmapped ? (
                        <p className="text-xs text-plantista-500 dark:text-plantista-400">
                          Not available for online purchase
                        </p>
                      ) : effectiveUnitPrice !== null ? (
                        <p className="text-xs text-plantista-500 dark:text-plantista-400">
                          ${effectiveUnitPrice.toFixed(2)} each
                        </p>
                      ) : (
                        <p className="text-xs text-amber-600 dark:text-amber-400">
                          Price unavailable
                        </p>
                      )}

                      {/* Quantity controls + line total on their own row */}
                      <div className="mt-2.5 flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() =>
                              cart.updateQuantity(
                                item.genusAliasId,
                                item.quantity - 1
                              )
                            }
                            disabled={item.quantity <= 1}
                            className="w-7 h-7 flex items-center justify-center rounded border border-gray-300 dark:border-plantista-700 text-plantista-700 dark:text-plantista-300 hover:bg-gray-100 dark:hover:bg-plantista-800 disabled:opacity-30 transition"
                          >
                            <Minus className="w-3 h-3" />
                          </button>
                          <input
                            type="text"
                            inputMode="numeric"
                            value={item.quantity}
                            onChange={(e) => {
                              const val = e.target.value.replace(/\D/g, "");
                              if (val === "") return;
                              const num = parseInt(val, 10);
                              if (num >= 1)
                                cart.updateQuantity(item.genusAliasId, num);
                            }}
                            onBlur={(e) => {
                              const val = parseInt(e.target.value, 10);
                              if (isNaN(val) || val < 1)
                                cart.updateQuantity(item.genusAliasId, 1);
                            }}
                            className="w-10 text-center text-sm font-semibold text-plantista-950 dark:text-white bg-transparent border border-gray-300 dark:border-plantista-700 rounded px-0 py-0.5 focus:outline-none focus:ring-1 focus:ring-plantista-500"
                          />
                          <button
                            onClick={() =>
                              cart.updateQuantity(
                                item.genusAliasId,
                                item.quantity + 1
                              )
                            }
                            className="w-7 h-7 flex items-center justify-center rounded border border-gray-300 dark:border-plantista-700 text-plantista-700 dark:text-plantista-300 hover:bg-gray-100 dark:hover:bg-plantista-800 transition"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>

                        <div className="text-right flex-shrink-0">
                          {priceLoading ? (
                            <div className="ml-auto h-4 w-12 rounded bg-gray-200 dark:bg-plantista-800 animate-pulse" />
                          ) : !excluded && effectiveUnitPrice !== null ? (
                            <p className="text-sm font-bold text-plantista-950 dark:text-white">
                              ${(effectiveUnitPrice * item.quantity).toFixed(2)}
                            </p>
                          ) : (
                            <p className="text-xs text-amber-600 dark:text-amber-400">
                              --
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              )}
            </div>
          )}
        </div>

        {/* Footer — subtotal + checkout */}
        {cart.items.length > 0 && (
          <div className="border-t border-gray-200 dark:border-plantista-800 px-6 py-4 space-y-3">
            {cart.hasUnpricedItems && (
              <p className="text-xs text-amber-600 dark:text-amber-400">
                Items without prices will be excluded from checkout.
              </p>
            )}

            <div className="flex justify-between items-center">
              <span className="text-sm text-plantista-600 dark:text-plantista-400">
                Subtotal ({cart.totalItems} items)
              </span>
              <span className="text-sm text-plantista-700 dark:text-plantista-300">
                ${displaySubtotal.toFixed(2)}
              </span>
            </div>
            {isShopify ? (
              // Shopify computes shipping + tax at its own checkout, so we no
              // longer show the hardcoded 8.30% estimate or a tax-inclusive total.
              <p className="text-xs text-plantista-500 dark:text-plantista-400">
                Shipping &amp; tax calculated at checkout
              </p>
            ) : (
              <>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-plantista-600 dark:text-plantista-400">
                    Est. Tax (8.30%)
                  </span>
                  <span className="text-sm text-plantista-700 dark:text-plantista-300">
                    ${(displaySubtotal * 0.083).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-gray-200 dark:border-plantista-800">
                  <span className="font-semibold text-plantista-900 dark:text-plantista-100">
                    Est. Total
                  </span>
                  <span className="text-xl font-bold text-plantista-950 dark:text-white">
                    ${(displaySubtotal * 1.083).toFixed(2)}
                  </span>
                </div>
              </>
            )}

            <div>
              <label className="block text-xs font-medium text-plantista-600 dark:text-plantista-400 mb-1">
                Order confirmation email
              </label>
              {email && !isEditingEmail ? (
                <div className="flex items-center justify-between px-3 py-2 rounded-lg border border-gray-200 dark:border-plantista-700 bg-gray-50 dark:bg-plantista-800">
                  <span className="text-sm text-plantista-950 dark:text-white truncate">
                    {email}
                  </span>
                  <button
                    onClick={() => setIsEditingEmail(true)}
                    className="ml-2 p-1 text-plantista-500 hover:text-plantista-700 dark:hover:text-plantista-300 transition flex-shrink-0"
                    aria-label="Edit email"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setEmailError("");
                  }}
                  onBlur={() => {
                    if (email) setIsEditingEmail(false);
                  }}
                  placeholder="Email for order confirmation"
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-plantista-700 bg-white dark:bg-plantista-800 text-plantista-950 dark:text-white placeholder-gray-400 dark:placeholder-plantista-500 focus:ring-2 focus:ring-plantista-500 focus:border-transparent outline-none transition"
                  autoFocus={isEditingEmail}
                />
              )}
              {emailError && (
                <p className="mt-1 text-xs text-red-500">{emailError}</p>
              )}
            </div>

            <button
              onClick={handleCheckout}
              disabled={
                isCheckingOut ||
                (isShopify ? purchasableCount === 0 : cart.subtotal === 0)
              }
              className="w-full py-3 bg-plantista-500 text-plantista-950 font-bold rounded-lg hover:bg-plantista-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCheckingOut
                ? "Redirecting to Checkout..."
                : "Proceed to Checkout"}
            </button>

            {/* AI-for-inspiration + horticultural/811/HOA notes — checkout
                surface (PLA2-338 / PLA2-371) */}
            <AiInspirationDisclaimer />
            <HorticulturalDisclaimer />
          </div>
        )}
      </div>
    </div>
  );
}
